import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { WifiOff, RefreshCw, CheckCircle2, X } from "lucide-react";

/**
 * OfflineBanner — muncul otomatis saat koneksi terputus
 * Posisi: fixed bottom, tidak mengganggu UI game anak
 */
export default function OfflineBanner() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [justCameBack, setJustCameBack] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const goOnline = () => {
      setIsOnline(true);
      setJustCameBack(true);
      // Sembunyikan "kembali online" setelah 3 detik
      setTimeout(() => setJustCameBack(false), 3000);
    };
    const goOffline = () => {
      setIsOnline(false);
      setJustCameBack(false);
      setDismissed(false); // Reset so they see it if they go offline again
    };

    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);

    // Listen untuk update SW
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.ready.then((reg) => {
        reg.addEventListener("updatefound", () => {
          const worker = reg.installing;
          if (worker) {
            worker.addEventListener("statechange", () => {
              if (worker.state === "installed" && navigator.serviceWorker.controller) {
                setUpdateAvailable(true);
              }
            });
          }
        });
      });
    }

    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  const handleUpdate = () => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.ready.then((reg) => {
        if (reg.waiting) {
          reg.waiting.postMessage({ type: "SKIP_WAITING" });
          window.location.reload();
        }
      });
    }
  };

  return (
    <AnimatePresence>
      {/* OFFLINE BANNER */}
      {!isOnline && !dismissed && (
        <motion.div
          key="offline"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 20 }}
          className="fixed bottom-4 left-4 right-4 z-[9999] flex items-center gap-3 px-4 py-3 rounded-2xl shadow-2xl"
          style={{
            background: "linear-gradient(135deg, #1e293b, #334155)",
            border: "2px solid #475569",
          }}
        >
          <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center flex-shrink-0">
            <WifiOff className="w-5 h-5 text-orange-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-white text-sm">
              Mode Offline 📴
            </div>
            <div className="text-slate-400 text-xs font-bold leading-tight">
              Kamu bisa tetap bermain! Data tersimpan otomatis saat online kembali.
            </div>
          </div>
          <button
            onClick={() => setDismissed(true)}
            className="p-1 rounded-full hover:bg-slate-700 transition-colors text-slate-400 hover:text-white"
            aria-label="Tutup"
          >
            <X className="w-5 h-5" />
          </button>
        </motion.div>
      )}

      {/* BACK ONLINE BANNER */}
      {isOnline && justCameBack && (
        <motion.div
          key="back-online"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 20 }}
          className="fixed bottom-4 left-4 right-4 z-[9999] flex items-center gap-3 px-4 py-3 rounded-2xl shadow-2xl"
          style={{
            background: "linear-gradient(135deg, #14532d, #166534)",
            border: "2px solid #16a34a",
          }}
        >
          <div className="w-10 h-10 rounded-full bg-green-400/20 flex items-center justify-center flex-shrink-0">
            <CheckCircle2 className="w-5 h-5 text-green-400" />
          </div>
          <div className="flex-1">
            <div className="font-display font-bold text-white text-sm">
              Terhubung Kembali! ✅
            </div>
            <div className="text-green-300 text-xs font-bold">
              Progress akan tersinkronisasi.
            </div>
          </div>
        </motion.div>
      )}

      {/* UPDATE AVAILABLE BANNER */}
      {updateAvailable && (
        <motion.div
          key="update"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 20 }}
          className="fixed bottom-4 left-4 right-4 z-[9999] flex items-center gap-3 px-4 py-3 rounded-2xl shadow-2xl cursor-pointer"
          style={{
            background: "linear-gradient(135deg, #1d4ed8, #2563eb)",
            border: "2px solid #3b82f6",
          }}
          onClick={handleUpdate}
        >
          <div className="w-10 h-10 rounded-full bg-blue-300/20 flex items-center justify-center flex-shrink-0">
            <RefreshCw className="w-5 h-5 text-blue-200" />
          </div>
          <div className="flex-1">
            <div className="font-display font-bold text-white text-sm">
              Update Tersedia! 🆕
            </div>
            <div className="text-blue-200 text-xs font-bold">
              Klik untuk memuat versi terbaru.
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
